import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { t } from '../../i18n';

export interface Column<T = any> {
  key: string;
  label: string;
  sortable?: boolean;
  align?: 'left' | 'right' | 'center';
  render?: (row: T) => unknown;
}

/**
 * <cadence-table .columns=${cols} .rows=${rows}> — sortable data table.
 * Set `.render` on a column to customize a cell. Emits `row-click` with the row.
 * Pagination is left to the host page (server-side for large lists).
 */
@customElement('cadence-table')
export class CadenceTable extends LitElement {
  @property({ type: Array }) columns: Column[] = [];
  @property({ type: Array }) rows: any[] = [];
  @property({ type: Boolean }) clickable = false;
  @state() private sortKey = '';
  @state() private sortDir: 'asc' | 'desc' = 'asc';

  static styles = css`
    :host { display: block; }
    .scroll { overflow-x: auto; border: 1px solid var(--color-border); border-radius: var(--radius-lg); background: var(--color-bg-primary); }
    table { width: 100%; border-collapse: collapse; font-size: 0.92rem; }
    th, td { text-align: left; padding: 12px 16px; border-bottom: 1px solid var(--color-border); }
    th { color: var(--color-text-secondary); font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.03em; user-select: none; white-space: nowrap; }
    th.sortable { cursor: pointer; }
    th.sortable:hover { color: var(--color-text-primary); }
    tbody tr:last-child td { border-bottom: none; }
    tbody tr.clickable { cursor: pointer; }
    tbody tr.clickable:hover { background: var(--color-bg-secondary); }
    .arrow { font-size: 0.7rem; opacity: 0.7; }
    .align-right { text-align: right; }
    .align-center { text-align: center; }
    .empty { padding: var(--space-6); text-align: center; color: var(--color-text-secondary); }
  `;

  private toggleSort(col: Column) {
    if (!col.sortable) return;
    if (this.sortKey === col.key) this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    else { this.sortKey = col.key; this.sortDir = 'asc'; }
  }

  private sorted(): any[] {
    if (!this.sortKey) return this.rows;
    const dir = this.sortDir === 'asc' ? 1 : -1;
    return [...this.rows].sort((a, b) => {
      const av = a[this.sortKey] ?? ''; const bv = b[this.sortKey] ?? '';
      return (av > bv ? 1 : av < bv ? -1 : 0) * dir;
    });
  }

  render() {
    if (!this.rows.length) {
      return html`<div class="scroll"><div class="empty">${t('common.states.empty')}</div></div>`;
    }
    return html`
      <div class="scroll">
        <table>
          <thead>
            <tr>
              ${this.columns.map((col) => html`
                <th class=${col.sortable ? 'sortable' : ''} @click=${() => this.toggleSort(col)}>
                  ${col.label}
                  ${this.sortKey === col.key ? html`<span class="arrow">${this.sortDir === 'asc' ? '▲' : '▼'}</span>` : ''}
                </th>`)}
            </tr>
          </thead>
          <tbody>
            ${this.sorted().map((row) => html`
              <tr class=${this.clickable ? 'clickable' : ''}
                  @click=${() => this.clickable && this.dispatchEvent(new CustomEvent('row-click', { detail: row, bubbles: true, composed: true }))}>
                ${this.columns.map((col) => html`
                  <td class=${col.align ? 'align-' + col.align : ''}>
                    ${col.render ? col.render(row) : row[col.key] ?? ''}
                  </td>`)}
              </tr>`)}
          </tbody>
        </table>
      </div>
    `;
  }
}
